import React, { useState, useMemo } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import Box from '@mui/material/Box';
import { TransportTitle } from '../../../../ui/styled';
import ScheduleDialog from './ScheduleDialog';
import { deleteStopApi } from '../../../../../services/StopApi/StopApi';
import { SCHEDULE_URL } from '../../../../../constants';

const SchedulesTable = ({ schedules, transportId, organizations, authToken, onScheduleChange }) => {
  const [openScheduleDialog, setOpenScheduleDialog] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [isNewSchedule, setIsNewSchedule] = useState(false);

  // Create organization lookup map for O(1) access
  const organizationMap = useMemo(() => {
    return new Map(organizations?.map(org => [org.orgId, org]) || []);
  }, [organizations]);

  const getOrganizationName = (orgId) => {
    return organizationMap.get(orgId)?.orgKey || '';
  };

  const handleAddSchedule = () => {
    setSelectedSchedule(null);
    setIsNewSchedule(true);
    setOpenScheduleDialog(true);
  };

  const handleEditSchedule = (schedule) => {
    setSelectedSchedule(schedule);
    setIsNewSchedule(false);
    setOpenScheduleDialog(true);
  };

  const handleDeleteSchedule = async (scheduleId) => {
    if (!window.confirm('Are you sure you want to delete this schedule line?')) {
      return;
    }

    try {
      const url = `${SCHEDULE_URL}/${scheduleId}`;
      const authorization = authToken;
      const response = await deleteStopApi({ url, authorization });

      if (response.succeeded) {
        onScheduleChange();
      } else {
        alert('Failed to delete schedule');
      }
    } catch (error) {
      console.error('Error deleting schedule:', error);
      alert('Error deleting schedule');
    }
  };

  const columns = [
    {
      field: 'departFrom',
      headerName: 'Depart From',
      flex: 1,
      minWidth: 150,
      valueGetter: (params) => getOrganizationName(params.value),
    },
    {
      field: 'departDate',
      headerName: 'Depart Date',
      width: 120,
      valueFormatter: (params) => {
        if (!params.value) return '';
        return new Date(params.value).toLocaleDateString();
      },
    },
    {
      field: 'departTime',
      headerName: 'Depart Time',
      width: 110,
    },
    {
      field: 'arrivedAt',
      headerName: 'Arrived At',
      flex: 1,
      minWidth: 150,
      valueGetter: (params) => getOrganizationName(params.value),
    },
    {
      field: 'arrivedDate',
      headerName: 'Arrived Date',
      width: 120,
      valueFormatter: (params) => {
        if (!params.value) return '';
        return new Date(params.value).toLocaleDateString();
      },
    },
    {
      field: 'arrivedTime',
      headerName: 'Arrived Time',
      width: 110,
    },
    {
      field: 'mode',
      headerName: 'Mode',
      width: 100,
    },
    {
      field: 'confirmed',
      headerName: 'Confirmed',
      width: 100,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton
            size="small"
            onClick={() => handleEditSchedule(params.row)}
            color="primary"
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleDeleteSchedule(params.row.sheduleId)}
            color="error"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <>
      <Box sx={{ width: '100%', mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <TransportTitle variant="h5">Schedule</TransportTitle>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={handleAddSchedule}
          >
            Add Schedule
          </Button>
        </Box>
        <DataGrid
          rows={schedules || []}
          columns={columns}
          getRowId={(row) => row.sheduleId}
          autoHeight
          disableSelectionOnClick
          disableColumnMenu
          pageSize={5}
          rowsPerPageOptions={[5, 10, 20]}
          density="compact"
          sx={{
            fontSize: '0.75rem !important',
            '& .MuiDataGrid-cell': {
              py: 0.5,
              fontSize: '0.75rem !important',
            },
            '& .MuiDataGrid-cellContent': {
              fontSize: '0.75rem !important',
            },
            '& .MuiDataGrid-row': {
              fontSize: '0.75rem !important',
            },
            '& .MuiDataGrid-columnHeader': {
              fontSize: '0.75rem !important',
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              fontSize: '0.75rem !important',
              fontWeight: 600,
            },
            '& .MuiDataGrid-footerContainer': {
              fontSize: '0.7rem !important',
            },
            '& .MuiTablePagination-root': {
              fontSize: '0.7rem !important',
            },
            '& .MuiIconButton-root': {
              padding: '4px',
              fontSize: '0.75rem !important',
            },
          }}
        />
      </Box>

      <ScheduleDialog
        open={openScheduleDialog}
        setOpen={setOpenScheduleDialog}
        schedule={selectedSchedule}
        isNew={isNewSchedule}
        transportId={transportId}
        organizations={organizations}
        authToken={authToken}
        onSave={onScheduleChange}
      />
    </>
  );
};

export default SchedulesTable;
